import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Badge } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import SidebarLink from './SidebarLink';
import SidebarCategory from './SidebarCategory';
import ClientSidebarContent from './ClientSidebarContent';
import shouldIncludeItem from './sidebarUtils';
import { SiteName, setFavicon, isDevelopment } from '../../../shared/components/domainSupport';
import { history } from '../../../helpers';

const amazonIcon = require('../../../shared/img/logo/amazonIcon.png');
const dspIcon = require('../../../shared/img/logo/dspIcon.png');
const walmartIcon = require('../../../shared/img/logo/walmartIcon.png');

export default function SidebarContent({
  changeToDark, changeToLight, changeToLimitless, changeDevPlatform, sidebar, clientMenuData,
}) {
  // Some sidebar is pulled in from a SERVER API
  const user = useSelector(state => state.authentication.user);
  const accessControl = JSON.parse(user.access_control);
  const sectionControl = clientMenuData?.sectionControl;
  const textAlignCenter = { paddingLeft: '20px' };
  const location = useLocation();
  const hasRedirected = useRef(false);

  if (!user.id) {
    history.push('/logout');
  }

  // Reset redirect flag when clientMenuData changes (account switch)
  useEffect(() => {
    hasRedirected.current = false;
  }, [clientMenuData]);

  // Check if there are any visible Self Service menu items
  const hasSelfServiceItems = useMemo(() => {
    if (!clientMenuData?.menuDetails) return false;

    return Object.values(clientMenuData.menuDetails).some((menuItem) => {
      if (menuItem.scope === 'selfService' && shouldIncludeItem(menuItem, user, accessControl)) {
        return true;
      }
      // Check content items
      if (menuItem.content) {
        return Object.values(menuItem.content).some((contentItem) => (
          contentItem.scope === 'selfService' && shouldIncludeItem(contentItem, user, accessControl)
        ));
      }
      return false;
    });
  }, [user, accessControl, clientMenuData]);

  // Collect visible routes from dynamic menu data (only restricted paths matter for redirect)
  const visibleRoutes = useMemo(() => {
    const routes = [];

    // Helper function to extract routes from menu data
    const extractRoutesFromMenuData = (menuData) => {
      if (!menuData) return;
      Object.values(menuData).forEach((menuItem) => {
        // Check menu item
        if (shouldIncludeItem(menuItem, user, accessControl)) {
          routes.push(menuItem.route);
        }
        // Check content items
        if (menuItem.content) {
          Object.values(menuItem.content).forEach((contentItem) => {
            if (shouldIncludeItem(contentItem, user, accessControl)) {
              routes.push(contentItem.route);
            }
          });
        }
      });
    };

    // Extract routes from client menu data (amazon, amc, dsp, walmart sections)
    extractRoutesFromMenuData(clientMenuData?.menuDetails);

    return routes;
  }, [
    user,
    accessControl,
    clientMenuData,
  ]);

  // Check if current route should be redirected (simplified version)
  useEffect(() => {
    // Only check when clientMenuData has loaded and we have visible routes, and haven't redirected yet
    if (clientMenuData && visibleRoutes && visibleRoutes.length > 0 && !hasRedirected.current) {
      const currentPath = location.pathname;

      // Always allow the "/" path
      if (currentPath === '/') {
        return;
      }

      // Path prefixes that should be checked for visibility
      const restrictedPrefixes = ['/amazon', '/walmart', '/dsp', '/amc'];

      // Check if current path starts with any restricted prefix
      const isRestrictedPath = restrictedPrefixes.some(prefix => currentPath.startsWith(prefix));

      // Only check visibility if it's a restricted path
      if (isRestrictedPath) {
        // Check if current path matches any visible route
        const hasMatchingRoute = visibleRoutes.some(route => {
          if (!route) return false;
          const [routePath] = route.split('#');
          return currentPath === routePath || currentPath.startsWith(routePath);
        });

        // If restricted path is not visible, redirect to first visible route or "/"
        if (!hasMatchingRoute) {
          const redirectTo = visibleRoutes[0] || '/';
          console.warn(`Current route ${currentPath} is not visible. Redirecting to ${redirectTo}`);
          hasRedirected.current = true;
          history.push(redirectTo);
        }
      }
    }
  }, [
    clientMenuData,
    location.pathname,
    visibleRoutes,
  ]);

  return (
    <div className="sidebar__content">
      <ul className="sidebar__block">
        {user.access === 'admin' && (accessControl.superRole || accessControl.adminAccountRole) && (
          <>
            <SidebarCategory title="Admin Accounts" scope="admin" icon="users" isAdmin>
              <SidebarLink title="Accounts All" route="/admin/accounts" />
              <SidebarLink title="Accounts Platform" route="/admin/account/status-all" />
              <SidebarLink title="Accounts View" route="/admin/account-view" />
              <SidebarLink title="Budget Monthly Preview" route="/admin/budget/monthly-preview" />
              <SidebarLink title="Calendar" route="/admin/event-calendar" />
              <SidebarLink title="Customers" route="/admin/customers" />
              <SidebarLink title="Users" route="/admin/users" />
            </SidebarCategory>
            <SidebarCategory title="Admin Data" scope="admin" icon="database" isAdmin>
              <SidebarLink title="Activity" route="/admin/event-activity" />
              <SidebarLink title="Data Check" route="/admin/data-check" />
              <SidebarLink title="File Loader" route="/admin/file-loader" />
              <SidebarLink title="Account Settings Data" route="/admin/account/settings" />
              <SidebarLink title="Insight Cards" route="/insight" betaLink newLink />
            </SidebarCategory>
            <SidebarCategory title="Account Config" scope="admin" icon="user" isAdmin>
              <SidebarLink title="AMC Instance" route="/admin/account/amc-instance" />
              <SidebarLink title="API Status" route="/admin/account/status" />
              <SidebarLink title="Settings" route="/admin/account/setting" />
              <SidebarLink title="Streams" route="/admin/account/streams" />
            </SidebarCategory>
          </>
        )}
        {accessControl.accountControlRole && (
          <SidebarCategory title="Account Controls" scope="admin" icon="lock" isAdmin>
            <SidebarLink title="Budget Control" route="/account-management/budget" />
            <SidebarLink title="AMZ Catalog Manager" route="/account-management/catalog-amazon" />
            <SidebarLink title="WMC Catalog Manager" route="/account-management/catalog-walmart" />
          </SidebarCategory>
        )}
        {user.access === 'admin' && (
          <SidebarCategory title="Account Team" scope="admin" icon="flag" isAdmin>
            {user.access === 'admin' && accessControl.superRole && (
              <SidebarCategory title="AMZ DSP" scope="admin">
                <SidebarLink title="Landing Pages" route="/account-management/amazon/dsp/landing-pages" />
                <SidebarLink title="Events" route="/account-management/amazon/dsp/events" />
                <SidebarLink title="Assets" route="/account-management/amazon/dsp/assets" />
                <SidebarLink title="Creatives" route="/account-management/amazon/dsp/creatives" />
                <SidebarLink title="Media Plans" route="/account-management/amazon/dsp/media-plans" />
                <SidebarLink title="Contextual Targets" route="/account-management/amazon/dsp/contextual-targets" />
                <SidebarLink title="Audience Targets" route="/account-management/amazon/dsp/audience-targets" />
              </SidebarCategory>
            )}
            <SidebarCategory title="AMZ Tools" scope="admin">
              <SidebarLink title="Off Products Check" route="/admin/data-check-apoi" />
              <SidebarLink title="Product Metrics" route="/account-management/product-metrics" />
            </SidebarCategory>
            <SidebarCategory title="AMZ Reports" scope="admin">
              <SidebarLink title="SB Campaigns" route="/admin/amz/sb/campaigns" />
            </SidebarCategory>
            <SidebarCategory title="Budget Tools" scope="admin">
              <SidebarLink title="AMZ Budget Forecast" route="/account-management/amz-budget-forecast" />
              <SidebarLink title="Budget Control" route="/self-service/budget" />
              <SidebarLink title="Budget Control OLD" route="/account-management/budget" />
              <SidebarLink title="Budget Stats" route="/account-management/budget-stats" />
              <SidebarLink title="WM Budget Forecast" route="/account-management/wm-budget-forecast" />
              <SidebarLink title="WM Search Trends" route="/customer/walmart-search-trends" />
              <SidebarLink title="AMZ Search Trends" route="/customer/amazon-search-trends" />
            </SidebarCategory>
            <SidebarCategory title="Catalog Tools" scope="admin">
              <SidebarLink title="AMZ Catalog Manager" route="/account-management/catalog-amazon" />
              <SidebarLink title="WMC Catalog Manager" route="/account-management/catalog-walmart" />
            </SidebarCategory>
            <SidebarLink title="Calendar" route="/customer/calendar" />
            <SidebarLink title="Contact Center" route="/account-management/contact-center" />
            <SidebarLink title="Todo Lists" route="/account-management/todo" />
          </SidebarCategory>
        )}
        {((user.access === 'agency' && !accessControl.selfServiceRole) || user.access === 'admin') && (
          <SidebarCategory title="Agency Team Legacy" scope="agency" icon="mustache" isLegacy>
            <SidebarLink title="Account API Status" route="/agency/account/status" />
            <SidebarCategory title="Budget Tools" scope="agency">
              <SidebarLink title="Budget Control" route="/agency/budget-legacy" />
              <SidebarLink title="AMZ Budget Forecast" route="/agency/amz-budget-forecast" />
              <SidebarLink title="WM Budget Forecast" route="/agency/wm-budget-forecast" />
            </SidebarCategory>
            <SidebarCategory title="Catalog Tools" scope="agency">
              <SidebarLink title="AMZ Catalog Manager" route="/agency/catalog-amazon" />
              <SidebarLink title="WMC Catalog Manager" route="/agency/catalog-walmart" />
            </SidebarCategory>
          </SidebarCategory>
        )}
      </ul>
      {hasSelfServiceItems && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>Self Service</h4>
          )}
          <ClientSidebarContent scope="selfService" menuDataFromServer={clientMenuData?.menuDetails} />
          {/* <SidebarLink title="Onboarding" route="/customer/onboarding" icon="magic-wand" /> */}
        </ul>
      )}
      <ul className="sidebar__block">
        <SidebarCategory title="Layout" scope="all" icon="layers">
          <button type="button" className="sidebar__link" onClick={() => changeToLight()}>
            <p className="sidebar__link-title">Light Theme</p>
          </button>
          <button type="button" className="sidebar__link" onClick={() => changeToDark()}>
            <p className="sidebar__link-title">Dark Theme</p>
          </button>
          <button type="button" className="sidebar__link" onClick={() => changeToLimitless()}>
            {setFavicon()}
            <p className="sidebar__link-title"><SiteName /> Theme</p>
          </button>
        </SidebarCategory>
      </ul>
      {user.access === 'basic' && (
        <ul className="sidebar__block">
          <SidebarLink title="Get Started" route="/customer/get-started" icon="key" />
          <SidebarLink title="Account Onboarding" route="/onboarding/ad-api/start" icon="key" />
        </ul>
      )}
      {sectionControl && sectionControl.amazon && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>Amazon Performance</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${amazonIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="amazon" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {sectionControl && sectionControl.amc && user.access === 'admin' && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>AMC Reports</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${dspIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="amc" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {sectionControl && sectionControl.dsp && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>DSP Performance</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${dspIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="dsp" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {sectionControl && sectionControl.google && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>Google Performance</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${dspIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="google" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {/* TODO: Update Google icon above from dspIcon to a proper Google icon */}
      {sectionControl && sectionControl.meta && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>Meta Performance</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${dspIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="meta" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {sectionControl && sectionControl.target && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>Target Performance</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${dspIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="target" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {sectionControl && sectionControl.walmart && (
        <ul className="sidebar__block">
          {sidebar && !sidebar.collapse && (
            <h4 style={textAlignCenter}>Walmart Performance</h4>
          )}
          {sidebar && sidebar.collapse && (
            <>
              <div
                type="button"
                className="sidebar__link"
                style={{
                  display: 'flex', margin: 'auto', width: '50px', background: `url(${walmartIcon})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'default',
                }}
              />
              <br />
            </>
          )}
          <ClientSidebarContent scope="walmart" menuDataFromServer={clientMenuData?.menuDetails} />
        </ul>
      )}
      {!sectionControl && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: sidebar && sidebar.collapse ? '60px' : '100px',
            padding: '20px 0',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              fontSize: sidebar && sidebar.collapse ? '24px' : '32px',
              fontWeight: 'bold',
              color: '#4ce1b6',
              letterSpacing: '4px',
              marginLeft: '-20px',
            }}
          >
            <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0s' }}>.</span>
            <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0.2s' }}>.</span>
            <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0.4s' }}>.</span>
            <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0.6s' }}>.</span>
          </div>
          <style>
            {`
              @keyframes dotPulse {
                0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-10px); }
              }
            `}
          </style>
        </div>
      )}
      <ul className="sidebar__block">
        {(user.access === 'admin' || user.email === 'dan@charbs.com') && (
          <SidebarCategory title="Launch Central" scope="demo" icon="rocket" isDev>
            <SidebarLink title="Launch Results" route="/customer/launch-results" />
            <SidebarLink title="Launch Settings" route="/customer/launch-settings" />
          </SidebarCategory>
        )}
        {user.access === 'admin' && (accessControl.superRole || accessControl.devRole) && (
          <SidebarCategory title="In Development" scope="admin" icon="cloud-sync" isDev>
            <SidebarLink title="Product Ads" betaLink route="/customer/product-ads" icon="layers" />
            <SidebarLink title="Vendor Inventory Health" devLink route="/amazon/vendor/inventory-health" />
            <SidebarLink title="JTSR Asin" devLink route="/jtsr/asin" />
            <SidebarLink title="Vendor PPC Stats" devLink route="/amazon/vendor/ppc-stats" />
            <SidebarLink title="Seller Asin Brand Performance" devLink route="/customer/seller-asin-brand-performance" />
          </SidebarCategory>
        )}
        {user.access === 'admin' && isDevelopment() && (
          <SidebarCategory title="Platform" scope="development" icon="store" isDev>
            <button type="button" className="sidebar__link" onClick={() => changeDevPlatform('limitless')}>
              <p className="sidebar__link-title">Limitless</p>
            </button>
            <button type="button" className="sidebar__link" onClick={() => changeDevPlatform('adjuggernaut')}>
              <p className="sidebar__link-title">AdJuggernaut</p>
            </button>
            <button type="button" className="sidebar__link" onClick={() => changeDevPlatform('adrev')}>
              <p className="sidebar__link-title">AdRev</p>
            </button>
          </SidebarCategory>
        )}
      </ul>
      {user.access === 'admin' && (
        <ul className="sidebar__block">
          <SidebarCategory title="Key Info" scope="development" icon="question-circle" isAdmin>
            <div style={{ paddingLeft: '15px', width: '100%' }}>
              <p><span className="sidebar__category-admin" style={{ display: 'inline-block' }} /> Admin Catagory</p>
              <p><span className="sidebar__category-agency" style={{ display: 'inline-block' }} /> Agency Catagory</p>
              <p><span className="sidebar__category-legacy" style={{ display: 'inline-block' }} /> Legacy Catagory</p>
              <p><span className="sidebar__category-beta" style={{ display: 'inline-block' }} /> Beta Catagory</p>
              <p><span className="sidebar__category-dev" style={{ display: 'inline-block' }} /> Development Catagory</p>
              <p><span className="sidebar__category-new" style={{ display: 'inline-block' }} /> New Catagory</p>
              <p><Badge className="sidebar__link-badge-admin"><span>ADMIN</span></Badge> Admin Link</p>
              <p><Badge className="sidebar__link-badge"><span>BETA</span></Badge> Beta Link</p>
              <p><Badge className="sidebar__link-badge-dev"><span>DEV</span></Badge> Development Link</p>
              <p><Badge className="sidebar__link-badge-new"><span>NEW</span></Badge> New Link</p>
            </div>
          </SidebarCategory>
        </ul>
      )}
    </div>
  );
}
